import React, { useState } from 'react';
import ClearIcon from '@material-ui/icons/Clear';
import {
    Checkbox,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
} from '@material-ui/core';
import { useTranslation } from '../../contexts/translationContext';
import { useStyles } from './styles';
import { Production } from '../../types/ProductionApiResponse';
import { Vsm } from '../../types/VsmApiResponse';
import { AuditType } from '../../types/AuditTypeApiResponse';
import { Zone } from '../../types/ZoneApiResponse';

interface Props {
    fiscalYears: number[];
    vsmList: Vsm[];
    productionList: Production[];
    zoneList: Zone[];
    defaultFilterParams: FilterParams;
    onFilterChange: (FilterParams) => void;
    onFiscalYearChange: (year: number | null) => void;
}

export interface FilterParams {
    fiscalYearsId: number | null;
    vsmIds: number[];
    productionIds: number[];
    zoneIds: number[];
}

export const ReportTasksFilter = ({
    fiscalYears,
    vsmList,
    productionList,
    zoneList,
    defaultFilterParams,
    onFilterChange,
    onFiscalYearChange,
}: Props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    // Lokální hodnoty filtru. Je nutné držet lokální kvůli kombinace select onChange onClose
    const [filterParams, setFilterParams] = useState<FilterParams>(defaultFilterParams);

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                minWidth: 450,
            },
        },
    };

    const handleChangeVsm = (value: number[]) => {
        // profiltrovat produkce
        let filteredProdIds: number[] = [];
        filterParams.productionIds.map((prodId: number) => {
            let prod = productionList.find((prod: Production) => prod.id === prodId);
            if (prod && value.includes(prod.parentVsmId))
                filteredProdIds = [...filteredProdIds, prodId];
        });
        setFilterParams({ ...filterParams, vsmIds: value, productionIds: filteredProdIds });
    };

    const handleChangeProduction = (value: number[]) => {
        setFilterParams({ ...filterParams, productionIds: value });
    };

    const handleChangeZone = (value: number[]) => {
        setFilterParams({ ...filterParams, zoneIds: value });
    };

    const handleChangeFiscalYear = (value: number | null) => {
        setFilterParams({ ...filterParams, fiscalYearsId: value });
        onFilterChange({ ...filterParams, fiscalYearsId: value });
    };

    // Při zavření selectu, chceme poslat filterData do nadřazené komponenty. Do té doby pracujeme s filtrem jen lokálně.
    const handleSelectClose = () => {
        onFilterChange(filterParams);
    };

    return (
        <Grid
            container
            spacing={0}
            style={{ width: '100%' }}
            direction="column"
            alignItems="center"
            justifyContent="center"
        >
            <Grid item sm={12}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="fiscal-years-label">
                        {t('Report.task.filter-fiscal-years')}
                    </InputLabel>

                    <Select
                        labelId="fiscal-years-label"
                        value={filterParams.fiscalYearsId}
                        onChange={(e) => handleChangeFiscalYear(e.target.value as number)}
                        // onClose={handleSelectClose}
                        input={<Input />}
                        IconComponent={() => (
                            <IconButton onClick={() => handleChangeFiscalYear(null)} size="small">
                                <ClearIcon />
                            </IconButton>
                        )}
                        MenuProps={MenuProps}
                    >
                        {fiscalYears.map((fiscalYear: number) => (
                            <MenuItem key={fiscalYear} value={fiscalYear}>
                                <ListItemText primary={fiscalYear} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item sm={12}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="vsm-label">{t('Report.task.filter-2')}</InputLabel>
                    <Select
                        labelId="vsm-label"
                        multiple
                        value={filterParams.vsmIds}
                        onChange={(e) => handleChangeVsm(e.target.value as number[])}
                        onClose={handleSelectClose}
                        input={<Input />}
                        renderValue={(selected) =>
                            (selected as number[])
                                .map(
                                    (item: number) => vsmList.find((x: Vsm) => x.id === item)?.name
                                )
                                .join(', ')
                        }
                        MenuProps={MenuProps}
                    >
                        {vsmList.map((vsm: Vsm) => (
                            <MenuItem key={vsm.id} value={vsm.id}>
                                <Checkbox
                                    checked={filterParams.vsmIds.some(
                                        (id: number) => id === vsm.id
                                    )}
                                />
                                <ListItemText primary={vsm.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item sm={12}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="production-label">{t('Report.task.filter-3')}</InputLabel>
                    <Select
                        labelId="production-label"
                        multiple
                        value={filterParams.productionIds}
                        onChange={(e) => handleChangeProduction(e.target.value as number[])}
                        onClose={handleSelectClose}
                        input={<Input />}
                        renderValue={(selected) =>
                            (selected as number[])
                                .map(
                                    (item: number) =>
                                        productionList.find((x: AuditType) => x.id === item)?.name
                                )
                                .join(', ')
                        }
                        MenuProps={MenuProps}
                    >
                        {productionList.map((production: Production) => (
                            <MenuItem key={production.id} value={production.id}>
                                <Checkbox
                                    checked={filterParams.productionIds.some(
                                        (id: number) => id === production.id
                                    )}
                                />
                                <ListItemText primary={production.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item sm={12}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="zone-label">{t('Report.task.filter-zone')}</InputLabel>
                    <Select
                        labelId="zone-label"
                        multiple
                        value={filterParams.zoneIds}
                        onChange={(e) => handleChangeZone(e.target.value as number[])}
                        onClose={handleSelectClose}
                        input={<Input />}
                        renderValue={(selected) =>
                            (selected as number[])
                                .map(
                                    (item: number) =>
                                        zoneList.find((x: Zone) => x.id === item)?.name
                                )
                                .join(', ')
                        }
                        MenuProps={MenuProps}
                    >
                        {zoneList.map((zone: Zone) => (
                            <MenuItem key={zone.id} value={zone.id}>
                                <Checkbox
                                    checked={filterParams.zoneIds.some(
                                        (id: number) => id === zone.id
                                    )}
                                />
                                <ListItemText primary={zone.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );
};
